import { useEffect, useMemo, useState } from 'react';
import { KIND_COLORS, LIGHTNING_CAVEAT, LIGHTNING_NEAR_CHOICES, TONE_SILENT } from '@ww/shared';
import type { AlertKind, AlertSoundPolicy, PublicMapLink, ToneDef } from '@ww/shared';
import { api } from '../api';
import { useLive } from '../hooks/useLive';
import { useAlertSound } from '../hooks/useAlertSound';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { errorMessage, isAdmin } from '../lib/status';
import { SNOOZE_CHOICES, formatSilenceRemaining } from '../lib/soundPrefs';
import { I, Icon } from '../lib/icons';
import { fmtDate } from '../lib/time';

/**
 * Settings: audible alerting.
 *
 * Two clearly separated halves, because conflating them is how this feature goes wrong.
 * The first card is THIS DEVICE — mute, snooze, volume, all stored in this browser. The
 * second is THE COUNTY — which tone each kind of alert makes, stored on the server,
 * audited, identical on every console. An operator muting their own desk must never
 * silence the dispatch floor.
 */

/** Order and wording an operator recognises, worst first. */
const KIND_ROWS: Array<{ kind: AlertKind; label: string; example: string }> = [
  { kind: 'tor', label: 'Tornado warning', example: 'Tornado Warning' },
  { kind: 'ice', label: 'Ice', example: 'Ice Storm Warning' },
  { kind: 'svr', label: 'Severe thunderstorm', example: 'Severe Thunderstorm Warning' },
  { kind: 'flood', label: 'Flood', example: 'Flash Flood Warning' },
  { kind: 'snow', label: 'Winter / snow', example: 'Winter Storm Warning' },
  { kind: 'watch', label: 'Watches', example: 'Tornado Watch' },
  { kind: 'sps', label: 'Statements and advisories', example: 'Special Weather Statement' },
];

export default function Settings() {
  const { me } = useLive();
  const admin = isAdmin(me?.user.role);
  const sound = useAlertSound();

  const [tones, setTones] = useState<ToneDef[] | null>(null);
  const [draft, setDraft] = useState<AlertSoundPolicy | null>(null);
  const [saved, setSaved] = useState<AlertSoundPolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tenantId = me?.tenant?._id ?? null;

  useEffect(() => {
    let cancelled = false;
    api
      .alertSounds()
      .then((r) => {
        if (cancelled) return;
        setTones(r.tones);
        setSaved(r.sounds);
        setDraft(r.sounds);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const dirty = useMemo(
    () => !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved],
  );

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.updateAlertSounds(draft);
      setSaved(r.sounds);
      setDraft(r.sounds);
      // The provider is what actually plays tones; without this the change would not
      // take effect on this screen until the next page load.
      await sound.reloadPolicy();
      setNotice('Alert sounds saved. Change recorded in the audit log.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <ErrorBox error={loadError} />;
  if (!draft || !tones) return <Loading label="Loading settings…" />;

  const setTone = (kind: AlertKind, toneId: string) =>
    setDraft((d) => (d ? { ...d, tones: { ...d.tones, [kind]: toneId } } : d));

  return (
    <div className="page page-scroll">
      <PageHeader title="Settings" sub="Audible alerting, for this device and for the county.">
        {dirty && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-ghost hov" onClick={() => setDraft(saved)} disabled={busy}>
              Discard
            </button>
            <button type="button" className="btn btn-primary hov" onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </PageHeader>

      {notice && <div className="ok-box">{notice}</div>}
      <ErrorBox error={error} />

      <ThisDeviceCard />

      <PublicMapCard admin={admin} tenantId={tenantId} />

      <section className="panel">
        <div className="panel-head">
          <span className="lbl">Tones for this county</span>
        </div>
        <div className="sound-body">
          <div className="muted" style={{ fontSize: 13 }}>
            These apply to every console signed in to {me?.tenant?.name ?? 'this county'}, including
            the wallboard. Muting your own machine is above, and is separate.
          </div>

          {!admin && (
            <div className="warn-box">
              You can see these settings but not change them. Ask a county administrator.
            </div>
          )}

          <div className="sound-switch">
            <div style={{ minWidth: 0 }}>
              <strong>Audible alerts</strong>
              <div className="muted" style={{ fontSize: 12.5 }}>
                Off silences every device for this county. Alerts, texts and the map are unaffected.
              </div>
            </div>
            <Toggle
              on={draft.enabled}
              label="Audible alerts enabled"
              onChange={admin ? (v) => setDraft((d) => (d ? { ...d, enabled: v } : d)) : undefined}
            />
          </div>

          <div className="sound-switch">
            <div style={{ minWidth: 0 }}>
              <strong>Also sound on updates</strong>
              <div className="muted" style={{ fontSize: 12.5 }}>
                Sound again when an alert already on screen is re-issued with new information. Off
                by default: a long winter event is re-issued often.
              </div>
            </div>
            <Toggle
              on={draft.soundOnChange}
              label="Sound on updates"
              onChange={admin ? (v) => setDraft((d) => (d ? { ...d, soundOnChange: v } : d)) : undefined}
            />
          </div>

          <div className="tone-rows">
            {KIND_ROWS.map(({ kind, label, example }) => (
              <div className="tone-row" key={kind}>
                <span className="tone-swatch" style={{ background: KIND_COLORS[kind].fill }} aria-hidden />
                <div style={{ minWidth: 0 }}>
                  <div className="tone-name">{label}</div>
                  <div className="muted tone-example">e.g. {example}</div>
                </div>
                <select
                  className="fld"
                  value={draft.tones[kind]}
                  disabled={!admin || !draft.enabled}
                  aria-label={`Tone for ${label}`}
                  onChange={(e) => setTone(kind, e.target.value)}
                >
                  <option value={TONE_SILENT}>Silent</option>
                  {tones.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary hov"
                  disabled={draft.tones[kind] === TONE_SILENT}
                  onClick={() => sound.preview(draft.tones[kind])}
                  aria-label={`Preview the ${label} tone`}
                >
                  <Icon d={I.play} size={14} /> Play
                </button>
              </div>
            ))}
          </div>

          <div className="muted" style={{ fontSize: 12.5 }}>
            When several alerts arrive at once, only the most serious one sounds.
          </div>

          <div style={{ borderTop: '1px solid #1e2c46', paddingTop: 14 }}>
            <strong>Lightning</strong>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              Sound once when lightning first comes within this distance of an enabled asset,
              and again only after it has moved well clear. {LIGHTNING_CAVEAT}
            </div>

            <div className="tone-row" style={{ marginTop: 10 }}>
              <span className="tone-swatch" style={{ background: '#ffe27a' }} aria-hidden />
              <div style={{ minWidth: 0 }}>
                <div className="tone-name">Lightning nearby</div>
                <div className="muted tone-example">
                  {draft.lightningNearMiles ? `within ${draft.lightningNearMiles} mi` : 'off'}
                </div>
              </div>
              <select
                className="fld"
                value={draft.lightningTone}
                disabled={!admin || !draft.enabled || draft.lightningNearMiles === null}
                aria-label="Tone for nearby lightning"
                onChange={(e) => setDraft((d) => (d ? { ...d, lightningTone: e.target.value } : d))}
              >
                <option value={TONE_SILENT}>Silent</option>
                {tones.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-secondary hov"
                disabled={draft.lightningTone === TONE_SILENT}
                onClick={() => sound.preview(draft.lightningTone)}
                aria-label="Preview the lightning tone"
              >
                <Icon d={I.play} size={14} /> Play
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {LIGHTNING_NEAR_CHOICES.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="chip hov"
                  disabled={!admin || !draft.enabled}
                  aria-pressed={draft.lightningNearMiles === m}
                  onClick={() => setDraft((d) => (d ? { ...d, lightningNearMiles: m } : d))}
                >
                  {m} mi
                </button>
              ))}
              <button
                type="button"
                className="chip hov"
                disabled={!admin || !draft.enabled}
                aria-pressed={draft.lightningNearMiles === null}
                onClick={() => setDraft((d) => (d ? { ...d, lightningNearMiles: null } : d))}
              >
                Off
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


/**
 * The county's public map link.
 *
 * Kept as a list rather than one field so a rotation can overlap: a county that printed
 * the link on a flyer needs the old code alive until the new one is out. Revoked codes
 * stay visible, greyed, because "when did we kill that link" is a question somebody asks.
 */
function PublicMapCard({ admin, tenantId }: { admin: boolean; tenantId: string | null }) {
  const [links, setLinks] = useState<PublicMapLink[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.publicMapLinks();
      setLinks(r.links ?? []);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
      setLinks([]);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(null), 2500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const urlFor = (code: string) => `${window.location.origin}/m/${code}`;

  const copy = async (code: string) => {
    const url = urlFor(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(code);
    } catch {
      // Clipboard access is refused outside a secure context; say so rather than
      // failing silently and leaving someone to wonder what the button did.
      setError(`Could not copy. The link is ${url}`);
    }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createPublicMapLink();
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.revokePublicMapLink(code);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const active = (links ?? []).filter((l) => !l.revokedAt);
  const revoked = (links ?? []).filter((l) => l.revokedAt);

  return (
    <section className="panel">
      <div className="panel-head">
        <span className="lbl">Public map</span>
      </div>
      <div className="sound-body">
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.55 }}>
          A public map link opens a read-only county map for anyone who has it — no sign-in. It
          shows current weather alerts, reported road closures, and <strong>only</strong> those
          assets explicitly marked "Show on public map" on the Assets page. Every other asset,
          along with notes, reporter names and notification settings, stays private.
        </div>

        {!admin && (
          <div className="warn-box">
            You can see these links but not create or revoke them. Ask a county administrator.
          </div>
        )}

        {error && <div className="err-box">{error}</div>}
        {copied && <div className="ok-box">Link copied to the clipboard.</div>}

        {links === null ? (
          <div className="muted" style={{ fontSize: 13 }}>Loading links…</div>
        ) : links.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No public map link exists for this county. Nothing is published.
          </div>
        ) : (
          <div className="pm-links">
            {active.map((l) => (
              <div className="pm-link" key={l.code}>
                <a className="mono pm-link-url" href={urlFor(l.code)} target="_blank" rel="noreferrer">
                  {urlFor(l.code)}
                </a>
                <span className="muted pm-link-meta">Created {fmtDate(l.createdAt)} by {l.createdBy}</span>
                <div className="pm-link-actions">
                  <button type="button" className="btn btn-sm btn-secondary hov" onClick={() => void copy(l.code)}>
                    Copy
                  </button>
                  {admin &&
                    (confirmRevoke === l.code ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger-solid"
                          disabled={busy}
                          onClick={() => void revoke(l.code)}
                        >
                          Revoke for good
                        </button>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmRevoke(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger hov"
                        disabled={busy}
                        onClick={() => setConfirmRevoke(l.code)}
                      >
                        Revoke
                      </button>
                    ))}
                </div>
              </div>
            ))}
            {/* Kept on screen, greyed: history of what was published and when it stopped. */}
            {revoked.map((l) => (
              <div className="pm-link is-revoked" key={l.code}>
                <span className="mono pm-link-url">{urlFor(l.code)}</span>
                <span className="muted pm-link-meta">Revoked {fmtDate(l.revokedAt)}</span>
                <div className="pm-link-actions" />
              </div>
            ))}
          </div>
        )}

        {admin && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary hov" disabled={busy} onClick={() => void create()}>
              {busy ? 'Working…' : 'Create link'}
            </button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              A new link is live the moment it is created. Revoking one stops it working for
              everybody, immediately and permanently.
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

/** Everything in here is stored in this browser and reaches nobody else. */
function ThisDeviceCard() {
  const { prefs, setPrefs, snooze, unmute, silenceMsLeft, audio, enableAudio, policy, preview } =
    useAlertSound();

  const snoozed = silenceMsLeft > 0;

  return (
    <section className="panel">
      <div className="panel-head">
        <span className="lbl">This device</span>
      </div>
      <div className="sound-body">
        <div className="muted" style={{ fontSize: 13 }}>
          Stored in this browser only. Nobody else is affected, and nothing here is audited.
        </div>

        {audio === 'unsupported' ? (
          <div className="warn-box">
            This browser cannot play tones. Alerts will still appear on screen.
          </div>
        ) : audio === 'blocked' ? (
          /*
           * Not an error and not a nag. Browsers refuse audio until the page has had a
           * real gesture, so an untouched wallboard is genuinely silent and has to be
           * told so — believing it is armed when it is not is the dangerous state.
           */
          <div className="warn-box" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200 }}>
              Sound is blocked until this page is clicked once — your browser requires it. Nothing
              will be heard on this screen until then.
            </span>
            <button type="button" className="btn btn-primary hov" onClick={() => void enableAudio()}>
              Enable sound
            </button>
          </div>
        ) : null}

        {!policy.enabled && (
          <div className="warn-box">
            Audible alerts are turned off for the whole county, below. Nothing here will sound.
          </div>
        )}

        <div className="sound-switch">
          <div style={{ minWidth: 0 }}>
            <strong>Mute this device</strong>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Stays muted until you turn it back on.
            </div>
          </div>
          <Toggle
            on={prefs.muted}
            label="Mute this device"
            onChange={(v) => setPrefs({ muted: v, mutedUntil: v ? null : prefs.mutedUntil })}
          />
        </div>

        <div>
          <div style={{ marginBottom: 6 }}>
            <strong>Mute for a while</strong>
            {snoozed && (
              <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
                muted for another {formatSilenceRemaining(silenceMsLeft)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SNOOZE_CHOICES.map((c) => (
              <button
                key={c.minutes}
                type="button"
                className="chip hov"
                disabled={prefs.muted}
                onClick={() => snooze(c.minutes)}
              >
                {c.label}
              </button>
            ))}
            {(snoozed || prefs.muted) && (
              <button type="button" className="chip hov" onClick={unmute}>
                Unmute now
              </button>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="ww-volume">
            <strong>Volume</strong>{' '}
            <span className="muted" style={{ fontSize: 13 }}>{Math.round(prefs.volume * 100)}%</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <input
              id="ww-volume"
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(prefs.volume * 100)}
              onChange={(e) => setPrefs({ volume: Number(e.target.value) / 100 })}
              style={{ flex: 1, maxWidth: 280 }}
            />
            <button type="button" className="btn btn-sm btn-secondary hov" onClick={() => preview('double-beep')}>
              <Icon d={I.play} size={14} /> Test
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
